import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { Sheet, Input } from "@/pages/Familia";
import { extractViviendaData, scoreVivienda } from "@/lib/viviendaMatch";
import { formatApiErrorDetail } from "@/lib/api";

// Sección "Pendientes de asignar": asuntos/documentos de hogar sin vivienda asignada.
export const PendientesAsignar = () => {
  const navigate = useNavigate();
  const { matters, documents, viviendas, asignarAsuntoAVivienda, createVivienda, vincularDocumentoVivienda, viviendaSinAsignar } = useData();
  const [expanded, setExpanded] = useState(null); // matter id con el selector de vivienda abierto
  const [busyId, setBusyId] = useState(null);
  const [createForm, setCreateForm] = useState(null);

  const pendientes = useMemo(
    () => matters.filter((m) => !m.vivienda_id && !m.vivienda_sin_asignar && (m.category === "Hogar" || m.vivienda_pendiente)),
    [matters]
  );
  // Documentos cuya revisión se cerró sin responder a qué vivienda corresponden.
  const docsPend = documents.filter((d) => d.vivienda_pendiente && !d.vivienda_id && !d.confirmado && !d.is_deleted);

  const resolver = async (id, fn, msg) => {
    setBusyId(id);
    try { await fn(); toast.success(msg); setExpanded(null); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar."); }
    finally { setBusyId(null); }
  };

  const primaryDoc = (m) => documents.find((d) => (m.documents || []).includes(d.id)) || null;

  const suggestionFor = (m) => {
    const doc = primaryDoc(m);
    if (!doc || !doc.analisis || viviendas.length === 0) return null;
    const data = extractViviendaData(doc.analisis);
    const cands = viviendas
      .map((v) => ({ viv: v, ...scoreVivienda(data, v) }))
      .filter((c) => c.reasons.length > 0 && c.confidence)
      .sort((a, b) => b.score - a.score);
    return cands[0] || null;
  };

  const assign = async (m, vivId) => {
    setBusyId(m.id);
    try {
      await asignarAsuntoAVivienda(m.id, vivId);
      const viv = viviendas.find((v) => v.id === vivId);
      toast.success(`Asignado a ${viv ? viv.alias : "la vivienda"}`);
      setExpanded(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo asignar.");
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = (m) => {
    const a = (primaryDoc(m)?.analisis?.vivienda) || {};
    const gv = (k) => (a[k] && a[k].valor) || "";
    const dir =
      gv("direccion_suministro") || gv("direccion_servicio") ||
      gv("inmueble_asegurado") || gv("direccion_contractual") || "";
    setCreateForm({
      matter: m,
      alias: gv("municipio") ? `Casa ${gv("municipio")}` : "",
      direccion: dir,
      municipio: gv("municipio"),
      provincia: gv("provincia"),
      codigo_postal: gv("codigo_postal"),
      referencia_catastral: gv("referencia_catastral"),
      aseguradora_hogar: gv("aseguradora"),
      numero_poliza: gv("numero_poliza"),
      tipo_tenencia: "propiedad", tipo_inmueble: "piso", uso: "vivienda habitual",
      titulares: "", notas: "", fecha_adquisicion: "", icon: "Home",
    });
  };

  const createAndAssign = async () => {
    if (!createForm.alias.trim()) return toast.error("Indica un alias para la vivienda.");
    if (!createForm.direccion.trim()) return toast.error("Indica la dirección de la vivienda.");
    const matter = createForm.matter;
    setBusyId(matter.id);
    try {
      const { matter: _m, ...payload } = createForm;
      const nueva = await createVivienda({ ...payload, alias: payload.alias.trim() });
      await asignarAsuntoAVivienda(matter.id, nueva.id);
      toast.success("Vivienda creada y asunto asignado");
      setCreateForm(null);
      setExpanded(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear la vivienda.");
    } finally {
      setBusyId(null);
    }
  };

  if (pendientes.length === 0 && docsPend.length === 0) return null;

  return (
    <section data-testid="pendientes-asignar" className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-[#1C2421]">
          <Icons.Inbox size={20} className="text-[#D97706]" /> Pendientes de asignar
        </h2>
        <span data-testid="pendientes-count" className="text-sm font-bold text-white bg-[#D97706] rounded-full px-2.5 py-0.5">
          {pendientes.length + docsPend.length}
        </span>
      </div>
      <p className="text-sm text-[#6E7A74] -mt-1">
        Documentos y asuntos de hogar que aún no están en ninguna vivienda. Asígnalos a la casa que corresponda.
      </p>

      {pendientes.map((m) => {
        const doc = primaryDoc(m);
        const sug = suggestionFor(m);
        const isOpen = expanded === m.id;
        return (
          <div key={m.id} data-testid={`pendiente-${m.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FEF3C7] flex items-center justify-center shrink-0">
                <Icons.FileText size={20} className="text-[#B45309]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-base text-[#1C2421] truncate">{m.title}</p>
                <p className="text-sm text-[#6E7A74] truncate">
                  {[m.provider, doc?.filename].filter(Boolean).join(" · ") || "Hogar"}
                </p>
                {sug && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1">
                      <Icons.Sparkles size={13} /> Sugerencia: {sug.viv.alias} ({sug.reasons.join(", ")})
                    </span>
                    <button data-testid={`pendiente-sug-${m.id}`} disabled={busyId === m.id}
                      onClick={() => assign(m, sug.viv.id)}
                      className="text-xs font-bold text-white bg-[#3A6B56] rounded-full px-3 py-1 hover:bg-[#2E5645] disabled:opacity-60">
                      Asignar a {sug.viv.alias}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <button data-testid={`pendiente-asignar-${m.id}`} onClick={() => setExpanded(isOpen ? null : m.id)}
                className="flex-1 min-w-[140px] min-h-[44px] rounded-xl bg-[#E8F0EC] text-[#2E5645] font-semibold text-sm border border-[#CDE3D6] hover:bg-[#DCEAE1] flex items-center justify-center gap-1.5">
                <Icons.Home size={16} /> Asignar a vivienda
              </button>
              <button data-testid={`pendiente-crear-${m.id}`} onClick={() => openCreate(m)}
                className="flex-1 min-w-[140px] min-h-[44px] rounded-xl bg-[#F2EDE4] text-[#4A5550] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#E5DFD5] flex items-center justify-center gap-1.5">
                <Icons.Plus size={16} /> Crear vivienda
              </button>
              <button data-testid={`pendiente-ver-${m.id}`} onClick={() => navigate(`/asunto/${m.id}`)}
                className="min-h-[44px] px-3 rounded-xl bg-white text-[#3A6B56] font-semibold text-sm border border-[#E5DFD5] hover:bg-[#F6F3EE] flex items-center justify-center gap-1.5">
                <Icons.Eye size={16} /> Ver
              </button>
            </div>

            {isOpen && (
              <div data-testid={`pendiente-picker-${m.id}`} className="mt-3 border-t border-[#E5DFD5] pt-3 space-y-2">
                {viviendas.length === 0 ? (
                  <p className="text-sm text-[#6E7A74]">Aún no tienes viviendas. Usa “Crear vivienda”.</p>
                ) : (
                  viviendas.map((v) => (
                    <button key={v.id} data-testid={`pendiente-pick-${m.id}-${v.id}`} disabled={busyId === m.id}
                      onClick={() => assign(m, v.id)}
                      className="w-full text-left min-h-[48px] rounded-xl border border-[#E5DFD5] px-4 py-2 flex items-center justify-between hover:border-[#3A6B56] transition-colors disabled:opacity-60">
                      <span className="min-w-0">
                        <span className="font-semibold text-[#1C2421]">{v.alias}</span>
                        {v.direccion ? <span className="text-sm text-[#6E7A74]"> · {v.direccion}</span> : null}
                      </span>
                      <Icons.ChevronRight size={18} className="text-[#C4BDB0] shrink-0" />
                    </button>
                  ))
                )}
                <button data-testid={`pendiente-sin-asignar-${m.id}`} disabled={busyId === m.id}
                  onClick={() => resolver(m.id, () => viviendaSinAsignar({ asunto_id: m.id }), "Guardado sin vivienda")}
                  className="w-full min-h-[44px] rounded-xl border border-[#E5DFD5] bg-white text-[#4A5550] font-semibold text-sm hover:bg-[#F2EDE4]">
                  Sin asignar
                </button>
                <button data-testid={`pendiente-dejar-${m.id}`} onClick={() => setExpanded(null)}
                  className="text-sm font-semibold text-[#6E7A74] min-h-[40px] hover:text-[#4A5550]">
                  Dejar pendiente
                </button>
              </div>
            )}
          </div>
        );
      })}

      {docsPend.map((d) => (
        <div key={d.id} data-testid={`pendiente-doc-${d.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
          <p className="font-heading font-bold text-base text-[#1C2421] truncate">{d.analisis?.titulo_sugerido?.valor || d.filename}</p>
          <p className="text-sm text-[#6E7A74] truncate">Documento sin terminar de guardar · {d.filename}</p>
          <div className="mt-3 space-y-2">
            {viviendas.map((v) => (
              <button key={v.id} data-testid={`pendiente-doc-pick-${d.id}-${v.id}`} disabled={busyId === d.id}
                onClick={() => resolver(d.id, () => vincularDocumentoVivienda(d.id, v.id), `Asignado a ${v.alias}`)}
                className="w-full text-left min-h-[48px] rounded-xl border border-[#E5DFD5] px-4 py-2 flex items-center justify-between hover:border-[#3A6B56] disabled:opacity-60">
                <span className="font-semibold text-[#1C2421]">{v.alias}</span>
                <Icons.ChevronRight size={18} className="text-[#C4BDB0] shrink-0" />
              </button>
            ))}
            <button data-testid={`pendiente-doc-sin-asignar-${d.id}`} disabled={busyId === d.id}
              onClick={() => resolver(d.id, () => viviendaSinAsignar({ doc_id: d.id }), "Guardado sin vivienda")}
              className="w-full min-h-[44px] rounded-xl border border-[#E5DFD5] bg-white text-[#4A5550] font-semibold text-sm hover:bg-[#F2EDE4]">
              Sin asignar
            </button>
          </div>
        </div>
      ))}

      {createForm && (
        <Sheet onClose={() => setCreateForm(null)} testid="pendiente-crear-form">
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-1">Crear vivienda y asignar</h2>
          <p className="text-sm text-[#6E7A74] mb-4">Hemos rellenado lo que detectamos en el documento. Revísalo y guarda.</p>
          <Input label="Alias" value={createForm.alias} onChange={(v) => setCreateForm({ ...createForm, alias: v })} testid="pendiente-crear-alias" placeholder="Ej. Casa Las Rozas" />
          <Input label="Dirección completa" value={createForm.direccion} onChange={(v) => setCreateForm({ ...createForm, direccion: v })} testid="pendiente-crear-direccion" placeholder="Calle, número, piso…" />
          <Input label="Municipio" value={createForm.municipio} onChange={(v) => setCreateForm({ ...createForm, municipio: v })} testid="pendiente-crear-municipio" placeholder="Opcional" />
          <Input label="Provincia" value={createForm.provincia} onChange={(v) => setCreateForm({ ...createForm, provincia: v })} testid="pendiente-crear-provincia" placeholder="Opcional" />
          <Input label="Código postal" value={createForm.codigo_postal} onChange={(v) => setCreateForm({ ...createForm, codigo_postal: v })} testid="pendiente-crear-cp" placeholder="Opcional" />
          <button data-testid="pendiente-crear-guardar" onClick={createAndAssign} disabled={busyId === createForm.matter.id}
            className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] disabled:opacity-60">
            {busyId === createForm.matter.id ? "Guardando…" : "Crear y asignar"}
          </button>
        </Sheet>
      )}
    </section>
  );
};
